import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useOutletContext, useParams } from "react-router";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FormContainer from "../../common/forms/FormContainer";
import SimpleGridContainer from "../../common/forms/SimpleGridContainer";
import BuyingCycleContext from "./BICommonComponents/BuyingCycleContext";
import BIOverviewCard, { OVERVIEW_CARD_TYPES } from "./BIOverviewComponents/BIOverviewCard";
import BICategoryCard from "./BIBuyPlanComponents/BICategoryCard";
import BIBuyPlanChartCard from "./BIBuyPlanComponents/BIBuyPlanChartCard";
import { requestBuyingIntelPageData } from "./buyingIntelPageApi";
import { BUYING_INTEL_PAGE_TYPES, DEFAULT_BUYING_CYCLE_ID } from "./buyingIntelPageConfig";
import {
    buildBuyingIntelMetricCardData,
    formatBuyingIntelNumber,
    formatBuyingIntelPercentage,
    formatBuyingIntelSignedNumber
} from "./buyingIntelDataUtils";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.biBuyPlanPage {
        display: flex;
        flex-direction: column;
        margin-top: 16px;
    }

    .category-allocation-container {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: flex-start;
        flex-wrap: wrap;
    }

    .category-grid {
        display: grid;
        grid-template-columns: repeat(5, minmax(130px, 1fr));
        gap: 12px;
        flex: 2;
        min-width: 500px;
    }

    .chart-section {
        flex: 1.2;
        min-width: 320px;
    }

    .category-allocation-container.is-stacked .category-grid,
    .category-allocation-container.is-stacked .chart-section {
        flex-basis: 100%;
        width: 100%;
    }

    @media (max-width: 1200px) {
        .category-grid {
            grid-template-columns: repeat(3, minmax(130px, 1fr));
        }
    }

    @media (max-width: 768px) {
        .category-grid {
            grid-template-columns: repeat(2, minmax(120px, 1fr));
        }
    }
`;
// #endregion

// #region CONSTANTS
const CATEGORY_ALLOCATION_SPLIT_WIDTH = 1200;
// #endregion

export default function BIBuyPlan() {
    document.title = `${APP_TITLE} - Buying Intelligence Buy Plan`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { buyingCycleID } = useParams();
    const navigations = useSelector(state => state.navigationAccesses);
    const activeBuyingCycleID = buyingCycleID || DEFAULT_BUYING_CYCLE_ID;
    const currentCompanyID = localStorage.getItem("currentCompanyID");
    const categoryAllocationContainerRef = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [pageData, setPageData] = useState(null);
    const [isBuyingValueStacked, setIsBuyingValueStacked] = useState(false);

    const contextData = pageData?.buyingCycle || {};
    const metrics = pageData?.metrics || {};
    const categoryAllocation = pageData?.categoryAllocation || {};
    const comparableSeasonName = contextData.comparableSeasonName || "Comparable season";

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties
    };
    // #endregion

    // #region COMPUTED PROPS
    const buyPlanMetricData = {
        buyingValue: buildBuyingIntelMetricCardData({
            plan: metrics.buyingValue?.plannedBudget,
            committedOrder: metrics.buyingValue?.committedOrder,
            comparableSeason: metrics.buyingValue?.comparableSeasonPurchased,
            unit: metrics.buyingValue?.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            changeValue: metrics.buyingValue?.changeVsComparable?.value,
            changePercentage: metrics.buyingValue?.changeVsComparable?.percentage,
            status: metrics.buyingValue?.status
        }),
        units: buildBuyingIntelMetricCardData({
            plan: metrics.units?.buyPlan,
            committedOrder: metrics.units?.committedOrder,
            comparableSeason: metrics.units?.comparableSeasonPurchased,
            changeValue: metrics.units?.changeVsComparable?.value,
            changePercentage: metrics.units?.changeVsComparable?.percentage,
            status: metrics.units?.status
        }),
        cost: buildBuyingIntelMetricCardData({
            plan: metrics.averageCost?.planned,
            comparableSeason: metrics.averageCost?.comparableSeason,
            unit: metrics.averageCost?.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            changeValue: metrics.averageCost?.changeVsComparable?.value,
            changePercentage: metrics.averageCost?.changeVsComparable?.percentage,
            status: metrics.averageCost?.status
        })
    };

    const { categories = [], totalBuyingValue = 0, currency = "", varianceCurrency = "" } = categoryAllocation;
    const categoryCards = categories.map(category => {
        let buyingValuePercentage = category.buyingValuePercentage;
        if (!Number.isFinite(buyingValuePercentage)) {
            buyingValuePercentage = totalBuyingValue ? (category.buyingValue / totalBuyingValue) * 100 : undefined;
        }

        return {
            key: category.categoryID,
            title: category.name,
            amount: `${formatBuyingIntelNumber(category.buyingValue)} ${currency}`,
            percentage: formatBuyingIntelPercentage(buyingValuePercentage)
        };
    });
    const categoryGridTarget = Math.max(15, Math.ceil(categoryCards.length / 5) * 5);
    const categoryPlaceholders = Array.from(
        { length: Math.max(0, categoryGridTarget - categoryCards.length) },
        (_, index) => ({
            key: `category-placeholder-${index}`,
            isPlaceholder: true
        })
    );
    const categoryGridItems = [...categoryCards, ...categoryPlaceholders];
    const chartLabels = categories.map(category => category.name.replaceAll("\n", " "));
    const chartSeries = categories.map(category => category.buyingValue);
    const varianceRows = categories
        .filter(category => Number.isFinite(category.varianceVsBuyPlan))
        .sort((categoryA, categoryB) => categoryA.varianceOrder - categoryB.varianceOrder)
        .map(category => ({
            key: category.categoryID,
            category: category.varianceName || category.name.replaceAll("\n", " "),
            variance: `${formatBuyingIntelSignedNumber(category.varianceVsBuyPlan)} ${varianceCurrency}`,
            tone: category.varianceTone
        }));
    const buyingValueChart = (
        <div className="chart-section">
            <BIBuyPlanChartCard
                labels={chartLabels}
                series={chartSeries}
                varianceRows={varianceRows}
            />
        </div>
    );
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        let isMounted = true;
        const handleLoader = loading => {
            if (isMounted) setIsLoading(loading);
        };

        setPageData(null);
        requestBuyingIntelPageData({
            pageType: BUYING_INTEL_PAGE_TYPES.BUY_PLAN,
            buyingCycleID: activeBuyingCycleID,
            companyID: currentCompanyID,
            setLoader: handleLoader,
            onError: () => handleLoader(false)
        }).then(data => {
            if (!isMounted || data === undefined) return;
            setPageData(data);
        });

        return () => {
            isMounted = false;
        };
    }, [activeBuyingCycleID, currentCompanyID]);

    useEffect(() => {
        setupHeaders(props, navigations, activeBuyingCycleID);
    }, [props?.layoutType, navigations, activeBuyingCycleID]);

    useLayoutEffect(() => {
        const container = categoryAllocationContainerRef.current;
        if (!container) return;

        const updateLayout = width => {
            setIsBuyingValueStacked(width < CATEGORY_ALLOCATION_SPLIT_WIDTH);
        };

        updateLayout(container.getBoundingClientRect().width);

        const resizeObserver = new ResizeObserver(([entry]) => {
            updateLayout(entry?.contentRect?.width ?? container.getBoundingClientRect().width);
        });
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [pageData]);
    // #endregion

    return (
        <>
            <FullScreenLoader isLoading={isLoading} />
            {pageData && (
                <StyledPage className="biBuyPlanPage">
                    {/* Top context section */}
                    <BuyingCycleContext data={contextData} />

                    {/* Metrics cards */}
                    <SimpleGridContainer type="minWidth" cardMinWidth="300px" gap="24px" autoFit>
                        <BIOverviewCard
                            cardType={OVERVIEW_CARD_TYPES.BUYING_VALUE}
                            data={buyPlanMetricData.buyingValue}
                            comparableSeasonName={comparableSeasonName}
                            showSecondary={false}
                        />
                        <BIOverviewCard
                            cardType={OVERVIEW_CARD_TYPES.UNITS}
                            data={buyPlanMetricData.units}
                            comparableSeasonName={comparableSeasonName}
                            primaryLabel="Planned units"
                            showSecondary={false}
                        />
                        <BIOverviewCard
                            cardType={OVERVIEW_CARD_TYPES.COST}
                            data={buyPlanMetricData.cost}
                            comparableSeasonName={comparableSeasonName}
                        />
                    </SimpleGridContainer>

                    {/* Category allocation grid & responsive buying value chart */}
                    <div ref={categoryAllocationContainerRef}>
                        <FormContainer title="RECOMMENDED CATEGORY ALLOCATION" style={{ marginTop: "24px" }}>
                            <div
                                className={`category-allocation-container ${
                                    isBuyingValueStacked ? "is-stacked" : ""
                                }`}>
                                <div className="category-grid">
                                    {categoryGridItems.map(category => (
                                        <BICategoryCard
                                            key={category.key}
                                            title={category.title}
                                            amount={category.amount}
                                            percentage={category.percentage}
                                            isPlaceholder={category.isPlaceholder}
                                        />
                                    ))}
                                </div>
                                {buyingValueChart}
                            </div>
                        </FormContainer>
                    </div>
                </StyledPage>
            )}
        </>
    );
}

// #region SMALL COMPONENTS
function setupHeaders(props, navigations, buyingCycleID) {
    const createMode = navigations?.navigationAccesses?.buyingIntel?.createModeOnly;
    const encodedBuyingCycleID = encodeURIComponent(buyingCycleID);

    props.setControlBarLeftBtns([
        {
            customSearch: <CustomSearch enabled={false} placeholder="Brand, season recommendation or order" />
        }
    ]);
    props.setControlBarCenterBtns([
        { to: "/buying-intel", active: true, disabled: false, title: "BUYING CYCLES" },
        { to: "/buying-intel/portfolio", active: false, title: "PORTFOLIO", disabled: true },
        { to: "/buying-intel/actions", active: false, title: "ACTIONS", disabled: true }
    ]);
    props.setControlBarRightBtns([
        { to: "/buying-intel", active: false, disabled: createMode, title: "View", icon: "eye" },
        {
            to: "/buying-intel/add",
            active: false,
            disabled: false,
            title: "Add",
            icon: "plus"
        },
        {
            to: `/buying-intel/overview/${encodedBuyingCycleID}`,
            active: false,
            disabled: false,
            title: "Overview",
            icon: "presentation-chart-01"
        },
        {
            to: `/buying-intel/buyplan/${encodedBuyingCycleID}`,
            active: true,
            disabled: false,
            title: "Buy plan",
            icon: "lightbulb-05"
        },
        {
            to: `/buying-intel/order/${encodedBuyingCycleID}`,
            active: false,
            disabled: true,
            title: "Order",
            icon: "scale-02"
        },
        {
            to: `/buying-intel/forecast/${encodedBuyingCycleID}`,
            active: false,
            disabled: true,
            title: "Forecast",
            icon: "line-chart-up-01"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: undefined
    });
}
// #endregion
